import { Lightning, Router } from '@lightningjs/sdk'
import { getColor, grey300, grey50, grey700, grey960, primary500 } from '../../constants/colors'
import { UniNeueRegular } from '../../constants/fonts'
import { BORDER_RADIUS } from '../../constants/general'
import { BASE_ROUTES } from '../../constants/routes'
import { LinkModel, TvButtonModel } from '../../types/flo-experience'
import { getIcon } from '../../utils/image'
import { captureClick, SEGMENT_TRACK_EVENTS } from '../../utils/segmentAnalytics'
import SpeechComp from '../speechComp/speechComp'

export class ActionButton extends SpeechComp {
  private _data: TvButtonModel
  private _action: LinkModel

  static _template() {
    return {
      w: 384,
      h: 84,
      rect: true,
      clipping: true,
      collision: true,
      color: getColor(primary500),
      shader: {
        type: Lightning.shaders.RoundedRectangle,
        radius: BORDER_RADIUS,
      },
      Fill: {
        x: -1,
        y: -1,
        w: (w) => w + 2,
        h: (h) => h + 2,
        rect: true,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: BORDER_RADIUS,
          fillColor: getColor(grey700),
        },
      },
      Wrapper: {
        w: (w) => w,
        h: (h) => h,
        flex: {
          direction: 'row',
          alignItems: 'center',
        },
        FilterIcon: {
          visible: false,
          color: getColor(grey300),
          texture: null,
          flexItem: { marginLeft: 32, marginRight: 24 },
        },
        Title: {
          y: 4,
          text: {
            text: '',
            fontSize: 32,
            fontFace: UniNeueRegular,
            textColor: getColor(grey300),
            wordWrap: false,
            wordWrapWidth: 208,
            textOverflow: 'ellipsis',
            maxLines: 1,
            maxLinesSuffix: '...',
          },
        },
        CancelIcon: {
          color: getColor(grey300),
          texture: Lightning.Tools.getSvgTexture(getIcon('icon-cancel'), 32, 32),
          visible: false,
          flexItem: { marginLeft: 32 },
        },
      },
    }
  }

  get filterIcon() {
    return this.tag('FilterIcon')
  }

  get title() {
    return this.tag('Title')
  }

  get cancelIcon() {
    return this.tag('CancelIcon')
  }

  get wrapper() {
    return this.tag('Wrapper')
  }

  get fill() {
    return this.tag('Fill')
  }

  get speechText(): string {
    return this.data?.title?.text
  }

  set data(v: TvButtonModel) {
    this._data = v

    this.renderButtonData(v)
  }

  get data() {
    return this._data
  }

  set action(v: LinkModel) {
    this._action = v
  }

  _handleEnter() {
    void captureClick(SEGMENT_TRACK_EVENTS.ButtonClicked, this.data?.action?.analytics)

    if (this._action?.url.includes(BASE_ROUTES.SPORT_FILTER) && this.data?.state === 'empty') {
      const sportsFilterUrl = this._action.url
      const encodedUrl = encodeURIComponent(sportsFilterUrl)
      Router.navigate(`${BASE_ROUTES.SPORT_FILTER}/${encodedUrl}`)
    } else if (this._action?.url.includes(BASE_ROUTES.SEARCH) && this.data?.state === 'filled') {
      const searchUrl = this._action?.url
      const encodedUrl = encodeURIComponent(searchUrl)
      Router.navigate(`${BASE_ROUTES.SEARCH}/${encodedUrl}`)
    } else if (this._action?.url.includes(BASE_ROUTES.EDIT_FAVORITES)) {
      const history = Router.getHistory()
      const previousPage = history[history.length - 1]
      const steps = previousPage.hash.includes('/sports/') ? -2 : -1
      Router.go(steps)
    }
  }

  _focus() {
    this.fill.patch({
      x: 6,
      w: (w) => w - 5,
      shader: { fillColor: getColor(grey50) },
    })
    this.wrapper.patch({
      FilterIcon: {
        color: getColor(grey960),
      },
      Title: {
        text: {
          textColor: getColor(grey960),
        },
      },
      CancelIcon: {
        color: getColor(grey960),
      },
    })
    this.speak()
  }

  _unfocus() {
    this.fill.patch({
      x: -1,
      w: (w) => w + 2,
      shader: { fillColor: getColor(grey700) },
    })
    this.wrapper.patch({
      FilterIcon: {
        color: getColor(grey300),
      },
      Title: {
        text: {
          textColor: getColor(grey300),
        },
      },
      CancelIcon: {
        color: getColor(grey300),
      },
    })
  }

  renderButtonData(data: TvButtonModel) {
    this._action = data?.action

    this.title.patch({
      text: {
        text: data?.title?.text ?? '',
      },
    })

    if (data?.icon?.style) {
      this.filterIcon.patch({
        visible: true,
        texture: Lightning.Tools.getSvgTexture(getIcon(data?.icon?.style), 32, 32),
      })
    } else {
      this.wrapper.patch({ flex: { justifyContent: 'center' } })
    }

    if (this.data?.state === 'filled') {
      this.cancelIcon.visible = true
    }
  }

  _handleHover() {
    this.fireAncestors('$updateFocusByHover', this)
    this._focus()
  }

  _handleUnhover() {
    this._unfocus()
  }

  _handleClick() {
    this._handleEnter()
  }
}
